import * as THREE from "three";
import debug from "debug";

const log = debug("app:fn");

function cleanup() {
  log("Cleaning up PartChooser...");

  // Remove event listeners
  this.eventHub.off("chooseDiffPart", this.handlePartSelection);

  // Clear references
  this.table = null;
  this.selectedObject = null;
  this.oldPartData = null;
  this.experience = null;
  this.scene = null;
  this.eventHub = null;

  log("PartChooser cleanup complete.");
}

/**
 * Class for managing the part selection and replacement process.
 * @category Experience
 * @subcategory ObjectManagement
 */
class PartChooser {
  /**
   * Constructs a PartChooser instance.
   * @param {Object} experience - The main application experience object.
   * @param {Object} experience.eventHub - Event hub for managing custom events.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   */
  constructor(experience) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.scene = experience.scene;
    this.table = null;
    this.filterInput = null;
    this.selectedObject = null;
    this.objectIdMap = experience?.undoRedoVault?.objectIdMap;

    this.init();
  }

  /**
   * Initializes event listeners.
   */
  init() {
    this.eventHub.on("chooseDiffPart", this.handlePartSelection.bind(this));
  }

  /**
   * Creates the PartChooser table and container elements.
   */
  createPartChooserTable() {
    // Create container using same positioning and styling as drag-table-container
    const container = document.createElement("div");
    container.classList.add("part-chooser-container");
    container.style.cssText = `
      position: absolute;
      z-index: 9998;
      top: 35%;
      left: 230px;
      width: auto;
      max-width: calc(100vw - 260px);
      height: auto;
      display: none;
      box-sizing: border-box;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
      border-radius: 15px;
      background-color: #fff;
      overflow: hidden;
    `;
    
    // Create title bar (minimal, like the regular table)
    const titleBar = document.createElement("div");
    titleBar.style.cssText = `
      background: #f2f2f2;
      padding: 5px 10px;
      border-bottom: 1px solid #ddd;
      font-weight: bold;
      font-size: 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: relative;
    `;
    
    // Create title text
    const titleText = document.createElement("span");
    titleText.textContent = "Choose Different Part";
    titleBar.appendChild(titleText);
    
    // Create close button (positioned in top right)
    const closeBtn = document.createElement("img");
    closeBtn.setAttribute("src", "./imgs/Close.png");
    closeBtn.style.cssText = `
      cursor: pointer;
      width: 16px;
      height: 16px;
      margin-left: auto;
      opacity: 0.85;
      position: relative;
      z-index: 10000;
    `;
    closeBtn.onclick = () => this.hidePartChooser();
    closeBtn.onmouseover = () => {
      closeBtn.style.opacity = "1";
    };
    closeBtn.onmouseout = () => {
      closeBtn.style.opacity = "0.85";
    };
    titleBar.appendChild(closeBtn);
    
    // Create filter div using drag-table-filter styling
    // Position it to avoid overlapping with the close button
    const filterDiv = document.createElement("div");
    filterDiv.classList.add("drag-table-filter");
    filterDiv.style.cssText = `
      position: absolute;
      display: flex;
      top: 5px;
      right: 35px;
      z-index: 9999;
    `;
    
    const filterInput = document.createElement("input");
    filterInput.type = "text";
    filterInput.placeholder = "Search parts...";
    filterInput.classList.add("drag-table-input");
    filterInput.addEventListener("input", (event) => {
      this.filterPartChooserRows(event.target.value);
    });
    filterDiv.appendChild(filterInput);
    this.filterInput = filterInput;
    
    // Create table using exact same styling as drag-table
    const table = document.createElement("table");
    table.classList.add("part-chooser-table", "drag-table");
    table.style.cssText = `
      position: relative;
      border-collapse: collapse;
      overflow-y: auto;
      overflow-x: hidden;
      display: block;
      z-index: 9999;
      background-color: #fff;
      top: 0px;
      left: 0px;
      margin: 10px auto;
      border: none;
      max-height: 400px;
      font-size: 12px;
      width: auto;
      min-width: 100%;
    `;
    
    // Assemble the container
    container.appendChild(titleBar);
    container.appendChild(filterDiv);
    container.appendChild(table);
    document.body.appendChild(container);
    
    this.table = table;
  }

  /**
   * Hides the PartChooser table.
   */
  hidePartChooser() {
    const container = document.querySelector(".part-chooser-container");
    if (container) {
      container.style.display = "none";
    }
  }

  /**
   * Fetches data from DataVault using events (same as Functions.js)
   */
  fetchFromDataVault(requestEvent, id, responseEvent) {
    return new Promise((resolve, reject) => {
      const responseHandler = (data) => {
        log(`Received data for ${responseEvent}:`, data);
        resolve({
          ok: true,
          headers: new Headers(),
          json: () => Promise.resolve({ data }), // Mimic the original response structure
        });
        this.eventHub.off(responseEvent, responseHandler); // Clean up the event listener
      };

      this.eventHub.on(responseEvent, responseHandler);
      log(`Emitting ${requestEvent} with id:`, id);
      this.eventHub.emit(requestEvent, id);
    });
  }

  /**
   * Handles the selection of a different part, updates the table and related UI, and replaces the selected part in the scene.
   */
  async handlePartSelection() {
    // Create or get PartChooser's own table (separate from regular UI table)
    this.table = document.querySelector(".part-chooser-table");
    
    if (!this.table) {
      // Create the PartChooser table and container
      this.createPartChooserTable();
    }
    
    // Ensure table structure exists - use exact same classes as regular table
    let thead = this.table.querySelector(".dragTable-head");
    let tbody = this.table.querySelector(".dragTable-body");
    
    if (!thead) {
      thead = document.createElement("thead");
      thead.classList.add("dragTable-head");
      this.table.appendChild(thead);
    }
    
    if (!tbody) {
      tbody = document.createElement("tbody");
      tbody.classList.add("dragTable-body");
      this.table.appendChild(tbody);
    }

    // Show PartChooser table and related UI elements
    this.table.style.display = "block";
    const partChooserContainer = document.querySelector(".part-chooser-container");
    if (partChooserContainer) {
      partChooserContainer.style.display = "block";
    }

    if (window.selectedForCM) {
      this.selectedObject = window.selectedForCM;
      
      // Debug: Log what PartChooser is receiving
      log("[DEBUG] PartChooser received window.selectedForCM:", this.selectedObject);
      log("[DEBUG] PartChooser selectedObject parent:", this.selectedObject.parent);
      log("[DEBUG] PartChooser selectedObject parent userData:", this.selectedObject.parent?.userData);

      // Get the position from the selected object itself (the Group)
      // The selectedObject IS the positioned part group in the scene
      const objectPosition = this.selectedObject.position.clone();
      
      // Get the rotation from the selected object itself
      const objectRotation = this.selectedObject.rotation.z;

      log("[DEBUG] Original part position (raw):", objectPosition);
      log("[DEBUG] Original part rotation:", objectRotation);
      
      // Use the same coordinate format as Functions.js (floored x,z like lastMouseup)
      const flooredX = Math.floor(objectPosition.x);
      const flooredZ = Math.floor(objectPosition.z);
      
      log("[DEBUG] Floored position like lastMouseup:", { x: flooredX, z: flooredZ, y: objectPosition.y });

      this.oldPartData = this.buildOldPartFigure({
        partData: this.selectedObject.userData,
        x: flooredX,
        y: -1.5, // Use the same y coordinate as Functions.js drag/drop
        z: flooredZ,
        angle: objectRotation,
      });

      if (!this.oldPartData) {
        console.warn("Failed to capture existing part data; replacement aborted.");
        return;
      }

      const catId = this.selectedObject.userData?.category;
      log("[DEBUG] PartChooser using category ID:", catId);

      try {
        if (!catId) {
          console.warn("Category ID is undefined. Cannot fetch part data.");
          return;
        }

        // Use the same data fetching approach as Functions.js
        const categoryDataResponse = await this.fetchFromDataVault("requestCategory", catId, "categoryData");
        
        if (!categoryDataResponse.ok) {
          this.table.style.display = "none";
          log(`No category data found for: ${catId}`);
          return;
        }
        
        const categoryResponseData = await categoryDataResponse.json();
        const categoryData = categoryResponseData.data;
        
        this.clearTable();
        this.populateTable(categoryData);
        if (this.filterInput) {
          this.filterPartChooserRows(this.filterInput.value);
        }
      } catch (error) {
        console.error("Error fetching or updating the table:", error);
      }

      window.selectedForCM = null;
    } else {
      console.warn("No object is selected for modification.");
    }
  }


  /**
   * Clears the contents of the table.
   */
  clearTable() {
    const thead = this.table.querySelector(".dragTable-head");
    const tbody = this.table.querySelector(".dragTable-body");
    
    if (thead) {
      thead.innerHTML = "";
    } else {
      log("Warning: .dragTable-head not found");
    }
    
    if (tbody) {
      tbody.innerHTML = "";
    } else {
      log("Warning: .dragTable-body not found");
    }
  }

  filterPartChooserRows(searchTerm = "") {
    if (!this.table) return;

    const query = searchTerm.toLowerCase();
    const rows = this.table.querySelectorAll(".dragTable-body tr");
    let visibleCount = 0;

    rows.forEach((row) => {
      const cells = Array.from(row.querySelectorAll("td"));
      const matches = query
        ? cells.some((cell) =>
            cell.textContent.toLowerCase().includes(query)
          )
        : true;

      row.style.display = matches ? "" : "none";
      if (matches) visibleCount += 1;
    });

    let emptyState = this.table.querySelector(".part-chooser-empty-state");
    if (!emptyState) {
      emptyState = document.createElement("tr");
      emptyState.className = "part-chooser-empty-state";
      const td = document.createElement("td");
      td.colSpan = this.table.querySelectorAll(".dragTable-head th").length || 1;
      td.textContent = "No parts match your search.";
      td.style.padding = "20px";
      td.style.textAlign = "center";
      emptyState.appendChild(td);
      this.table.querySelector(".dragTable-body").appendChild(emptyState);
    }

    emptyState.style.display = visibleCount === 0 ? "" : "none";
  }

  buildOldPartFigure(baseFigure) {
    if (!baseFigure?.partData || !this.selectedObject) {
      return null;
    }

    let clonedPartData;
    try {
      clonedPartData = JSON.parse(JSON.stringify(baseFigure.partData));
    } catch (error) {
      log("Failed to clone part data for undo tracking:", error);
      clonedPartData = { ...baseFigure.partData };
    }

    let objectId = clonedPartData.objectId || this.selectedObject.userData?.objectId;

    if (!objectId && this.objectIdMap instanceof Map) {
      for (const [id, uuid] of this.objectIdMap.entries()) {
        if (uuid === this.selectedObject.uuid) {
          objectId = id;
          break;
        }
      }
    }

    if (!objectId) {
      log("No objectId found for selected object; undo/redo tracking unavailable.");
      return null;
    }

    clonedPartData.objectId = objectId;

    return {
      partData: clonedPartData,
      x: baseFigure.x,
      y: baseFigure.y,
      z: baseFigure.z,
      angle: baseFigure.angle,
    };
  }

  buildNewPartFigure({ pnValue, formFactor, ffLayer, properties }) {
    const oldCategory =
      this.oldPartData?.partData?.category || this.selectedObject?.userData?.category;

    const mergedProperties = {
      ...properties,
      PN: pnValue,
    };

    return {
      partData: {
        fullpartnumber: pnValue,
        layer: ffLayer ?? this.oldPartData?.partData?.layer,
        properties: mergedProperties,
        category: oldCategory,
        ffId: formFactor?.ffIdx || this.oldPartData?.partData?.ffId,
      },
      x: this.oldPartData?.x ?? 0,
      y: this.oldPartData?.y ?? -1.5,
      z: this.oldPartData?.z ?? 0,
      angle: this.oldPartData?.angle ?? 0,
    };
  }

  /**
   * Populates the table with category data using the same approach as Functions.js
   * @param {Object} data - The data used to populate the table.
   */
  populateTable(data) {
    const thead = this.table.querySelector(".dragTable-head");
    const tbody = this.table.querySelector(".dragTable-body");

    if (!thead || !tbody) {
      log("Table structure is incomplete.");
      return;
    }

    // Create header row (same as in Functions.js)
    const headerRow = document.createElement("tr");
    data.headers.forEach((header) => {
      const th = document.createElement("th");
      th.textContent = header.lbl;
      // Use the exact same styling as drag-table th elements
      th.style.cssText = `
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
        background-color: #f2f2f2;
        position: sticky;
        top: 0;
        white-space: nowrap !important;
      `;

      // Add sort icon (same as in Functions.js)
      const sortIcon = document.createElement("img");
      sortIcon.setAttribute("src", "./imgs/sort_asc.png");
      sortIcon.style.marginLeft = "5px";
      sortIcon.style.cursor = "pointer";
      th.appendChild(sortIcon);
      
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);

    // Create table body (same logic as Functions.js)
    data.values.forEach((rowData) => {
      // Skip empty rows like Functions.js does
      const shouldIncludeRow = rowData.some(value => value !== false);
      if (!shouldIncludeRow) return;
      
      const row = document.createElement("tr");
      // Remove custom hover effects - let CSS handle it with drag-table tbody tr:hover
      
      // Store headers for later use (same as Functions.js)
      const headersJSON = JSON.stringify(data.headers.map(header => header.lbl));
      row.setAttribute("data-headers", headersJSON);
      
      // Create cells
      rowData.forEach((value, index) => {
        const td = document.createElement("td");
        td.classList.add("dragTable-cell"); // Use same class as regular table
        td.setAttribute("data-header", data.headers[index].lbl);
        // dragTable-cell CSS will handle the styling automatically
        
        // Check if value is undefined or empty (same as Functions.js)
        if (value === undefined || value === "") {
          td.textContent = "";
        } else {
          td.textContent = value;
        }
        row.appendChild(td);
      });
      
      // Add mouseover event for image preview (simplified version of Functions.js logic)
      row.addEventListener("mouseover", async () => {
        const pnValue = rowData[0];
        
        try {
          // Find form factor info from the data (same as Functions.js)
          const ffIdObject = data.pnToffId.find(pnToff => pnToff.pn === pnValue);
          
          if (ffIdObject) {
            const formFactor = data.formFactors.find(ff => ff.ffIdx === ffIdObject.ffId);
            
            if (formFactor && formFactor.images && formFactor.images.length > 0) {
              const imageInfo = formFactor.images.find(image => image.imageURL.includes("/hyb/"));
              
              if (imageInfo) {
                // Set image dimensions (same as Functions.js)
                let imgHeight = formFactor.h * 48 + "px";
                let imgWidth = formFactor.w * 48 + "px";
                
                const imagePreviewContainer = document.getElementById("image-preview-container");
                imagePreviewContainer.innerHTML = "";
                
                const imageElement = document.createElement("img");
                imageElement.classList.add("hover-image-preview");
                imageElement.style.width = imgWidth;
                imageElement.style.height = imgHeight;
                
                // Use the same image loading approach as Functions.js
                const imageUrl = `https://layoutxmw-assets.xmicrowave.com/cascade${imageInfo.imageURL}`;
                imageElement.src = imageUrl;
                
                imagePreviewContainer.appendChild(imageElement);
                imagePreviewContainer.style.display = "block";
              }
            }
          }
        } catch (error) {
          log("Error showing image preview:", error);
        }
      });
      
      // Hide image on mouseout
      row.addEventListener("mouseout", () => {
        document.getElementById("image-preview-container").style.display = "none";
      });
      
      // Add click handler for part selection (same logic as Functions.js)
      row.addEventListener("click", () => {
        const clickedRowData = rowData;
        log("Selected part:", clickedRowData);
        
        const headers = JSON.parse(row.getAttribute("data-headers"));
        const properties = headers.reduce((obj, header, index) => {
          obj[header] = clickedRowData[index] || "N/A";
          return obj;
        }, {});
        
        const pnValue = clickedRowData[0];
        
        // Find form factor info (same as Functions.js)
        const ffIdObject = data.pnToffId.find(pnToff => pnToff.pn === pnValue);
        
        if (ffIdObject) {
          const formFactor = data.formFactors.find(ff => ff.ffIdx === ffIdObject.ffId);
          
          if (formFactor && formFactor.images && formFactor.images.length > 0) {
            log("[DEBUG] Starting part replacement");
            log("[DEBUG] Old part position:", this.oldPartData);
            log("[DEBUG] New part number:", pnValue);

            if (!this.oldPartData?.partData?.objectId) {
              log("Cannot replace part without a valid objectId on the original part.");
              return;
            }

            const newPartFigure = this.buildNewPartFigure({
              pnValue,
              formFactor,
              ffLayer: ffIdObject.layer,
              properties,
            });

            this.eventHub.emit("logEvent", {
              eventType: "replace",
              data: {
                oldPart: this.oldPartData,
                newPart: newPartFigure,
              },
            });
          } else {
            log("Form factor or images not found for", pnValue);
          }
        } else {
          log("Part number not found in pnToffId:", pnValue);
        }
        
        // Hide PartChooser UI elements
        this.hidePartChooser();
        document.getElementById("image-preview-container").style.display = "none";
      });
      
      tbody.appendChild(row);
    });
  }
}

export default PartChooser;
